import React, {useEffect, useRef} from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import {normalizeEditorHtml, toEditorHtml} from "./rich-text-utils";
import "./rich-text.css";

// The text editor of descriptions, reports and notes (docs/tasks/25-rich-text-editor.md): bold, italic, underline,
// strike, numbered and bulleted lists, links; Enter starts a new paragraph. Right-to-left, each paragraph follows its own
// language. `value` is HTML (or an old plain text, which opens as paragraphs); `onChange` gets the HTML, or "" when
// nothing is written. Pasted text keeps only these formats.

const TOOLBAR = [
    ["bold", "italic", "underline", "strike"],
    [{list: "ordered"}, {list: "bullet"}],
    ["link"],
    ["clean"],
];
const FORMATS = ["bold", "italic", "underline", "strike", "list", "link"];
const TITLES = {
    bold: "پررنگ",
    italic: "کج",
    underline: "زیرخط",
    strike: "خط‌خورده",
    link: "پیوند",
    clean: "حذف قالب",
};

function RichTextEditor({value = "", onChange, placeholder = "", readOnly = false, minHeight = "7rem", className = "", autoFocus = false, ariaLabel}) {
    const hostRef = useRef(null);
    const quillRef = useRef(null);
    const lastHtml = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;
        host.innerHTML = "";
        const container = document.createElement("div");
        host.appendChild(container);
        const quill = new Quill(container, {
            theme: "snow",
            modules: {toolbar: TOOLBAR},
            formats: FORMATS,
            placeholder,
            readOnly,
        });
        quillRef.current = quill;

        // Persian tooltips on the toolbar buttons
        host.querySelectorAll(".ql-toolbar button").forEach((button) => {
            const name = [...button.classList].find((item) => item.startsWith("ql-"))?.slice(3);
            if (name === "list") button.title = button.value === "ordered" ? "فهرست شماره‌دار" : "فهرست گلوله‌ای";
            else if (TITLES[name]) button.title = TITLES[name];
        });
        if (ariaLabel) quill.root.setAttribute("aria-label", ariaLabel);

        const initial = toEditorHtml(value);
        if (initial) quill.setContents(quill.clipboard.convert({html: initial}), "silent");
        lastHtml.current = normalizeEditorHtml(initial ? quill.getSemanticHTML() : "");

        quill.on("text-change", () => {
            const html = normalizeEditorHtml(quill.getSemanticHTML());
            if (html === lastHtml.current) return;
            lastHtml.current = html;
            onChangeRef.current?.(html);
        });
        if (autoFocus) setTimeout(() => quill.focus(), 0);

        return () => {
            quillRef.current = null;
            host.innerHTML = "";
        };
        // the editor is created once; later values are applied below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // a value changed from outside (a form reset, a reloaded record) replaces the content; our own echo does not
    useEffect(() => {
        const quill = quillRef.current;
        if (!quill) return;
        const next = toEditorHtml(value);
        if (normalizeEditorHtml(next) === lastHtml.current) return;
        quill.setContents(next ? quill.clipboard.convert({html: next}) : [], "silent");
        lastHtml.current = normalizeEditorHtml(next ? quill.getSemanticHTML() : "");
    }, [value]);

    useEffect(() => {
        quillRef.current?.enable(!readOnly);
    }, [readOnly]);

    return <div ref={hostRef} className={`zr-editor ${readOnly ? "is-readonly" : ""} ${className}`} style={{"--zr-min-height": minHeight}} dir="rtl"/>;
}

export default RichTextEditor;
